
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import Eye from "../../form/Eye";
import TagInput from "../../form/TagInput";
import ErrorMessage from "../../form/ErrorMessage";
import Loader from "../../loader";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  clearHireTalentFormData,
  getProfile,
  getToken,
  setProfile,
  updateHireTalentFormData,
} from "../../../utils/pip";
import { useNavigate } from "react-router-dom";
import { hireTalentPaths } from "../../../routes/hire-talent";
import {
  fetchHireTalentProfile,
  hireTalentSignUp,
  updateHireTalentSection2,
  updateHireTalentSection3,
  updateHireTalentSection4,
} from "../../../redux/features/hire-talent/actions/authAction";
import { normalizeHireTalentProfile } from "../../../utils/hireTalentProfile";
import { handlePostLoginFlow } from "../../../utils/postLoginFlow";
import {
  hireTalentSignupStep1Schema,
  hireTalentSignupStep2Schema,
  hireTalentSignupStep3Schema,
} from "../../../utils/schema";

const RegisterStep = ({ onClose, initialStep = 1 }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { subLoading } = useSelector((state) => state.hireTalent?.auth);
  const [activeStep, setActiveStep] = useState(initialStep || 1);
  const [isEye1, setIsEye1] = useState(false);
  const [isEye2, setIsEye2] = useState(false);
  const [apiValues, setApiValues] = useState({});

  const normalizePhoneValue = (value) => {
    if (!value) return "";
    const trimmed = String(value).trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("+")) return trimmed;
    const digits = trimmed.replace(/\D/g, "");
    if (!digits) return "";
    return `+${digits}`;
  };

  const mapEmployeesToCompanySize = (value = "") => {
    if (!value) return "";
    const cleaned = String(value).trim();
    const map = {
      "1-10": "Startup (1–10 employees)",
      "11-50": "Growing Company (11–50 employees)",
      "51-200": "Scaling Company (51–200 employees)",
      "201-500": "Large Company (201–500 employees)",
      "500+": "Enterprise (500+ employees)",
    };
    return map[cleaned] || cleaned;
  };

  const applyProfileToValues = (data = {}) => {
    const hire = data.hiretalent || {};
    setApiValues({
      full_name:
        data.full_name ||
        `${data.first_name || ""} ${data.last_name || ""}`.trim(),
      email: data.email || "",
      password: "",
      confirm_password: "",
      company_name: hire.company_name || "",
      industry: hire.industry || "",
      company_description: hire.company_bio || "",
      company_size: mapEmployeesToCompanySize(hire.number_of_employees || ""),
      company_website: hire.company_website || "",
      linkedin_company: hire.linkedin_company_page || "",
      country: hire.country || "",
      city: hire.city || "",
      primary_contact_name: hire.primary_contact_full_name || "",
      primary_contact_title: hire.primary_contact_job_title || "",
      primary_contact_email: hire.primary_contact_work_email || "",
      primary_contact_phone: hire.primary_contact_phone_number || "",
      secondary_contact_name: hire.secondary_contact_full_name || "",
      secondary_contact_title: hire.secondary_contact_job_title || "",
      secondary_contact_email: hire.secondary_contact_work_email || "",
      secondary_contact_phone: hire.secondary_contact_phone_number || "",
      talent_types: [],
      talent_other: "",
      engagement_types: [],
      project_title: "",
      role_in_project: "",
      project_description: "",
      project_skills: [],
      required_skills: "",
      project_start_date: "",
      project_duration: "",
      team_member_required: "",
      estimated_budget: "",
      work_environment: "Remote",
      preferred_timezone: "",
      communication_tools: [],
      communication_other: "",
      collaboration_type: "",
      consent_accuracy: false,
      consent_contact: false,
      rating: "4.7",
      projects_posted: "18",
    });
  };

  useEffect(() => {
    if (!initialStep) return;
    setActiveStep(initialStep || 1);
  }, [initialStep]);

  useEffect(() => {
    const token = getToken("hire-talent");
    if (!token) return;
    dispatch(fetchHireTalentProfile({ force: true })).then((res) => {
      const data = res?.payload?.data || {};
      applyProfileToValues(data);
    });
  }, [dispatch]);

  const initialValues = useMemo(
    () => ({
      full_name: apiValues?.full_name || "",
      email: apiValues?.email || "",
      password: apiValues?.password || "",
      confirm_password: apiValues?.confirm_password || "",
      company_name: apiValues?.company_name || "",
      industry: apiValues?.industry || "",
      company_description: apiValues?.company_description || "",
      company_size: apiValues?.company_size || "",
      company_website: apiValues?.company_website || "",
      linkedin_company: apiValues?.linkedin_company || "",
      country: apiValues?.country || "",
      city: apiValues?.city || "",
      primary_contact_name: apiValues?.primary_contact_name || "",
      primary_contact_title: apiValues?.primary_contact_title || "",
      primary_contact_email: apiValues?.primary_contact_email || "",
      primary_contact_phone: apiValues?.primary_contact_phone || "",
      secondary_contact_name: apiValues?.secondary_contact_name || "",
      secondary_contact_title: apiValues?.secondary_contact_title || "",
      secondary_contact_email: apiValues?.secondary_contact_email || "",
      secondary_contact_phone: apiValues?.secondary_contact_phone || "",
      talent_types: apiValues?.talent_types || [],
      talent_other: apiValues?.talent_other || "",
      engagement_types: apiValues?.engagement_types || [],
      project_title: apiValues?.project_title || "",
      role_in_project: apiValues?.role_in_project || "",
      project_description: apiValues?.project_description || "",
      project_skills: apiValues?.project_skills || [],
      required_skills: apiValues?.required_skills || "",
      project_start_date: apiValues?.project_start_date || "",
      project_duration: apiValues?.project_duration || "",
      team_member_required: apiValues?.team_member_required || "",
      estimated_budget: apiValues?.estimated_budget || "",
      work_environment: apiValues?.work_environment || "Remote",
      preferred_timezone: apiValues?.preferred_timezone || "",
      communication_tools: apiValues?.communication_tools || [],
      communication_other: apiValues?.communication_other || "",
      collaboration_type: apiValues?.collaboration_type || "",
      consent_accuracy: apiValues?.consent_accuracy || false,
      consent_contact: apiValues?.consent_contact || false,
      rating: apiValues?.rating || "4.7",
      projects_posted: apiValues?.projects_posted || "18",
    }),
    [apiValues]
  );

  const industryOptions = [
    "Technology / SaaS",
    "Marketing & Advertising",
    "Finance / Fintech",
    "E-commerce",
    "Consulting",
    "Education",
    "Healthcare",
    "Other",
  ];

  const companySizeOptions = [
    "Startup (1–10 employees)",
    "Growing Company (11–50 employees)",
    "Scaling Company (51–200 employees)",
    "Large Company (201–500 employees)",
    "Enterprise (500+ employees)",
  ];

  const countryOptions = ["USA", "India", "United Kingdom", "Canada", "Australia", "UAE"];

  const talentTypeOptions = [
    "Graphic Designer",
    "Web Developer",
    "Mobile App",
    "Marketing Specialist",
    "Copywriter",
    "Data Analyst",
    "Video Editor",
    "UI/UX Designer",
    "Other",
  ];

  const engagementTypeOptions = [
    "Freelance project",
    "Part-time contract",
    "Long-term contract",
    "Full-time remote",
  ];

  const projectDurationOptions = [
    "Less than 1 month",
    "1–3 months",
    "3–6 months",
    "6+ months",
  ];

  const timezoneOptions = ["UTC", "GMT", "PST", "MST", "CST", "EST", "IST"];

  const communicationToolsOptions = [
    "Slack",
    "Microsoft Teams",
    "Zoom",
    "Google Meet",
    "Other",
  ];

  const steps = [1, 2, 3];

  const mapCompanySizeToEmployees = (value = "") => {
    if (!value) return "";
    const numbers = value.match(/\d+/g) || [];
    if (value.includes("+") && numbers[0]) return `${numbers[0]}+`;
    if (numbers.length >= 2) return `${numbers[0]}-${numbers[1]}`;
    return value;
  };

  const buildHiringNeeds = (values) => {
    const items = [...(values.talent_types || [])];
    if (values.talent_other?.trim()) items.push(values.talent_other.trim());
    return items.join(", ");
  };

  const buildEngagementType = (values) => {
    return (values.engagement_types || []).join(", ");
  };

  const buildCommunicationTools = (values) => {
    const items = (values.communication_tools || []).filter((item) => item !== "Other");
    if (values.communication_other?.trim()) items.push(values.communication_other.trim());
    return items.join(", ");
  };

  const handleNext = (values) => {
    const nextValues = {
      ...values,
      required_skills: (values.project_skills || []).join(", "),
    };
    updateHireTalentFormData(nextValues);
    if (activeStep === 1) {
      if (!nextValues.confirm_password) {
        toast.error("Please enter confirm password.");
        return;
      }
      if (nextValues.password !== nextValues.confirm_password) {
        toast.error("Your password must match.");
        return;
      }
      const payload = {
        email: nextValues.email,
        password: nextValues.password,
        full_name: nextValues.full_name,
        company_name: nextValues.company_name,
        company_website: nextValues.company_website,
        number_of_employees: mapCompanySizeToEmployees(nextValues.company_size),
        country: nextValues.country,
        city: nextValues.city,
        company_bio: nextValues.company_description,
        linkedin_company_page: nextValues.linkedin_company,
      };
      const callback = (response) => {
        const ok = response?.success || response?.data?.success;
        if (!ok) {
          const msg =
            response?.message ||
            response?.data?.message ||
            "Unable to complete signup.";
          toast.error(msg);
          return;
        }
        updateHireTalentFormData({
          ...nextValues,
          signup_step: 2,
        });
        handlePostLoginFlow({
          response,
          dispatch,
          navigate,
          onClose,
          skipCloseOnHireTalentModal: true,
        });
      };
      dispatch(hireTalentSignUp({ payload, callback }));
      return;
    }

    if (activeStep === 2) {
      const payload = {
        primary_contact_full_name: nextValues.primary_contact_name,
        primary_contact_job_title: nextValues.primary_contact_title,
        primary_contact_work_email: nextValues.primary_contact_email,
        primary_contact_phone_number: nextValues.primary_contact_phone,
        secondary_contact_full_name: nextValues.secondary_contact_name,
        secondary_contact_job_title: nextValues.secondary_contact_title,
        secondary_contact_work_email: nextValues.secondary_contact_email,
        secondary_contact_phone_number: nextValues.secondary_contact_phone,
      };
      const callback = (response) => {
        if (!response?.success) {
          toast.error(response?.message || "Unable to save contact details.");
          return;
        }
        updateHireTalentFormData({
          ...nextValues,
          signup_step: 3,
        });
        setActiveStep(3);
      };
      dispatch(updateHireTalentSection2({ payload, callback }));
      return;
    }

    setActiveStep((prev) => Math.min(prev + 1, steps.length));
  };

  const handleBack = (values) => {
    updateHireTalentFormData(values);
    setActiveStep((prev) => Math.max(prev - 1, 1));
  };

  const refreshHireTalentFromProfile = async () => {
    const res = await dispatch(fetchHireTalentProfile({ force: true }));
    const data = res?.payload?.data;
    if (!data) return;
    applyProfileToValues(data);
  };

  const handlePrevious = async (values) => {
    updateHireTalentFormData(values);
    await refreshHireTalentFromProfile();
    setActiveStep((prev) => Math.max(prev - 1, 1));
  };

  const handleFinish = (values) => {
    if (activeStep < steps.length) {
      handleNext(values);
      return;
    }
    const nextValues = {
      ...values,
      required_skills: (values.project_skills || []).join(", "),
    };
    updateHireTalentFormData(nextValues);
    const payloadSection3 = {
      hiring_needs: buildHiringNeeds(nextValues),
      engagement_type: buildEngagementType(nextValues),
      projects: [
        {
          project_title: nextValues.project_title,
          role_in_project: nextValues.role_in_project,
          project_categories: nextValues.project_skills || [],
          team_member_required: nextValues.team_member_required
            ? String(nextValues.team_member_required)
            : null,
          start_date: nextValues.project_start_date || null,
          end_date: null,
          project_duration: nextValues.project_duration || null,
          duration_type: "month",
          budget: nextValues.estimated_budget
            ? Number(nextValues.estimated_budget)
            : null,
          project_description: nextValues.project_description,
          status: "pending",
        },
      ],
    };

    const payloadSection4 = {
      work_enviroment: nextValues.work_environment || "Remote",
      preferred_timezone: nextValues.preferred_timezone,
      communication_tools_used: buildCommunicationTools(nextValues),
      types_of_collaboration: nextValues.collaboration_type,
      information_confirmed: nextValues.consent_accuracy,
      agree_contact_opportunities: nextValues.consent_contact,
    };

    const callbackSection4 = (response) => {
      if (!response?.success) {
        toast.error(response?.message || "Unable to save work environment details.");
        return;
      }

      dispatch(fetchHireTalentProfile({ force: true })).then((res) => {
        const data = res?.payload?.data;
        if (data) {
          const existing = getProfile("hire-talent") || {};
          const normalized = normalizeHireTalentProfile({
            ...existing,
            ...data,
          });
          setProfile("hire-talent", normalized);
        }
      });

      clearHireTalentFormData();
      if (onClose) onClose();
      navigate(hireTalentPaths.Dashboard);
    };

    const callbackSection3 = (response) => {
      if (!response?.success) {
        toast.error(response?.message || "Unable to save project details.");
        return;
      }
      dispatch(updateHireTalentSection4({ payload: payloadSection4, callback: callbackSection4 }));
    };

    dispatch(updateHireTalentSection3({ payload: payloadSection3, callback: callbackSection3 }));
  };

  return (
    <div class="modal-body pe-0">
      {subLoading && <Loader />}
      <section class="ct_signup_main py-3">
        <div class="container-fluid">
          <div class="row align-items-center">
            <div class="col-xxl-4 col-xl-4 col-lg-4 mb-4 mb-lg-0 d-none d-lg-block">
              <div class="ct_signup_left_img">
                <img
                  src="../assets/img/hire_talent_signup.png"
                  alt=""
                  style={{ objectFit: "cover" }}
                />
              </div>
            </div>
            <div class="col-xxl-8 col-xl-8 col-lg-8 mb-4 mb-lg-0 ">
              <div class="ct_signup_right_content px-5 ct_custom_scroll">
                <div class="ct_signup_logo ct_mb_34">
                  <img src="../assets/img/Nuvio_logo.png" alt="" />
                </div>
                <div class="mt-0 text-center">
                  <h2 class="ct_fs_32 mb-0 ct_darkblue_text">
                    Build your company profile on{" "}
                    <span class="ct_yellow_text">Nuvio</span>
                  </h2>
                  <p className="mb-0 ct_fs_16 mt-2 ct_darkblue_text mx-auto">
                    Share company details so we can match you with the right talent.
                  </p>
                </div>

                <Formik
                  initialValues={initialValues}
                  validationSchema={
                    activeStep === 1
                      ? hireTalentSignupStep1Schema
                      : activeStep === 2
                        ? hireTalentSignupStep2Schema
                        : hireTalentSignupStep3Schema
                  }
                  onSubmit={() => { }}
                  enableReinitialize
                  validateOnBlur
                  validateOnChange
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    setTouched,
                    setFieldError,
                    setFieldTouched,
                    validateForm,
                  }) => (
                    <form class="mt-5" onSubmit={handleSubmit}>
                      {activeStep === 1 && (
                        <div className="row">
                          <div className="col-12">
                            <h6 className="ct_fs_16 ct_darkblue_text mb-3">
                              Account Information
                            </h6>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Full Name
                              </label>
                              <input
                                type="text"
                                className="form-control ct_input"
                                placeholder="Full Name"
                                id="full_name"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.full_name}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="full_name"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Work Email
                              </label>
                              <input
                                type="email"
                                className="form-control ct_input"
                                placeholder="Work Email"
                                id="email"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.email}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="email"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Password
                              </label>
                              <div className="position-relative">
                                <input
                                  id="password"
                                  className="form-control ct_input pe-5"
                                  placeholder="Password"
                                  type={isEye1 ? "text" : "password"}
                                  value={values.password}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                />
                                <Eye isEye={isEye1} onClick={setIsEye1} />
                              </div>
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="password"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Confirm Password
                              </label>
                              <div className="position-relative">
                                <input
                                  id="confirm_password"
                                  className="form-control ct_input pe-5"
                                  placeholder="Confirm Password"
                                  type={isEye2 ? "text" : "password"}
                                  value={values.confirm_password}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                />
                                <Eye isEye={isEye2} onClick={setIsEye2} />
                              </div>
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="confirm_password"
                              />
                            </div>
                          </div>

                          <div className="col-12">
                            <h6 className="ct_fs_16 ct_darkblue_text mb-3">
                              Company Information
                            </h6>
                          </div>
                          <div className="col-md-12">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Company Name
                              </label>
                              <input
                                type="text"
                                className="form-control ct_input"
                                placeholder="Company Name"
                                id="company_name"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.company_name}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="company_name"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Industry
                              </label>
                              <select
                                className="form-control ct_input"
                                id="industry"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.industry}
                              >
                                <option value="">Select Industry</option>
                                {industryOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="industry"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Company Size
                              </label>
                              <select
                                className="form-control ct_input"
                                id="company_size"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.company_size}
                              >
                                <option value="">Select Size</option>
                                {companySizeOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="company_size"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Company Website
                              </label>
                              <input
                                type="text"
                                className="form-control ct_input"
                                placeholder="Company Website"
                                id="company_website"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.company_website}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="company_website"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                LinkedIn Company Page
                              </label>
                              <input
                                type="text"
                                className="form-control ct_input"
                                placeholder="LinkedIn Company Page"
                                id="linkedin_company"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.linkedin_company}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="linkedin_company"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Country
                              </label>
                              <select
                                className="form-control ct_input"
                                id="country"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.country}
                              >
                                <option value="">Select Country</option>
                                {countryOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="country"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                City
                              </label>
                              <input
                                type="text"
                                className="form-control ct_input"
                                placeholder="City"
                                id="city"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.city}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="city"
                              />
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Company Description
                              </label>
                              <textarea
                                rows="4"
                                className="form-control ct_input h-auto"
                                placeholder="Tell us about your company"
                                id="company_description"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.company_description}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="company_description"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {activeStep === 2 && (
                        <div className="row">
                          <div className="col-12">
                            <h6 className="ct_fs_16 ct_darkblue_text mb-3">
                              Contact Person (Primary)
                            </h6>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Full Name
                              </label>
                              <input
                                type="text"
                                className="form-control ct_input"
                                placeholder="Full Name"
                                id="primary_contact_name"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.primary_contact_name}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="primary_contact_name"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Job Title
                              </label>
                              <input
                                type="text"
                                className="form-control ct_input"
                                placeholder="Job Title"
                                id="primary_contact_title"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.primary_contact_title}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="primary_contact_title"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Work Email
                              </label>
                              <input
                                type="email"
                                className="form-control ct_input"
                                placeholder="Work Email"
                                id="primary_contact_email"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.primary_contact_email}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="primary_contact_email"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Phone Number
                              </label>
                              <PhoneInput
                                id="primary_contact_phone"
                                className="ct_phone_input"
                                defaultCountry="MU"
                                value={normalizePhoneValue(values.primary_contact_phone)}
                                onChange={(val) => {
                                  const safeVal = val ?? "";
                                  setFieldValue("primary_contact_phone", safeVal);
                                }}
                                onBlur={() => {
                                  setFieldTouched("primary_contact_phone", true);
                                  if (
                                    values.primary_contact_phone &&
                                    !isValidPhoneNumber(values.primary_contact_phone)
                                  ) {
                                    setFieldError(
                                      "primary_contact_phone",
                                      "Please enter valid phone number"
                                    );
                                  }
                                }}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="primary_contact_phone"
                              />
                            </div>
                          </div>

                          <div className="col-12">
                            <h6 className="ct_fs_16 ct_darkblue_text mb-3">
                              Contact Person (Secondary)
                            </h6>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Full Name
                              </label>
                              <input
                                type="text"
                                className="form-control ct_input"
                                placeholder="Full Name"
                                id="secondary_contact_name"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.secondary_contact_name}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="secondary_contact_name"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Job Title
                              </label>
                              <input
                                type="text"
                                className="form-control ct_input"
                                placeholder="Job Title"
                                id="secondary_contact_title"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.secondary_contact_title}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="secondary_contact_title"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Work Email
                              </label>
                              <input
                                type="email"
                                className="form-control ct_input"
                                placeholder="Work Email"
                                id="secondary_contact_email"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.secondary_contact_email}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="secondary_contact_email"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Phone Number
                              </label>
                              <PhoneInput
                                id="secondary_contact_phone"
                                className="ct_phone_input"
                                defaultCountry="MU"
                                value={normalizePhoneValue(values.secondary_contact_phone)}
                                onChange={(val) => {
                                  const safeVal = val ?? "";
                                  setFieldValue("secondary_contact_phone", safeVal);
                                }}
                                onBlur={() => {
                                  setFieldTouched("secondary_contact_phone", true);
                                  if (
                                    values.secondary_contact_phone &&
                                    !isValidPhoneNumber(values.secondary_contact_phone)
                                  ) {
                                    setFieldError(
                                      "secondary_contact_phone",
                                      "Please enter valid phone number"
                                    );
                                  }
                                }}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="secondary_contact_phone"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {activeStep === 3 && (
                        <div className="row">
                          <div className="col-12">
                            <h6 className="ct_fs_16 ct_darkblue_text mb-3">
                              Hiring Needs
                            </h6>
                          </div>
                          <div className="col-12">
                            <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                              What type of talent are you looking for?
                            </label>
                            <div className="d-flex flex-wrap gap-3 mb-4">
                              {talentTypeOptions.map((option) => (
                                <label key={option} className="d-flex align-items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={values.talent_types.includes(option)}
                                    onChange={(event) => {
                                      if (event.target.checked) {
                                        setFieldValue("talent_types", [
                                          ...values.talent_types,
                                          option,
                                        ]);
                                      } else {
                                        setFieldValue(
                                          "talent_types",
                                          values.talent_types.filter((item) => item !== option)
                                        );
                                      }
                                    }}
                                  />
                                  <span className="ct_fs_14 ct_darkblue_text">{option}</span>
                                </label>
                              ))}
                            </div>
                            {values.talent_types.includes("Other") && (
                              <div className="form-group mb-4">
                                <input
                                  type="text"
                                  className="form-control ct_input"
                                  placeholder="Other, please specify"
                                  id="talent_other"
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  value={values.talent_other}
                                />
                                <ErrorMessage
                                  errors={errors}
                                  touched={touched}
                                  fieldName="talent_other"
                                />
                              </div>
                            )}
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="talent_types"
                            />
                          </div>
                          <div className="col-12">
                            <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                              Engagement Type
                            </label>
                            <div className="d-flex flex-wrap gap-3 mb-4">
                              {engagementTypeOptions.map((option) => (
                                <label key={option} className="d-flex align-items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={values.engagement_types.includes(option)}
                                    onChange={(event) => {
                                      if (event.target.checked) {
                                        setFieldValue("engagement_types", [
                                          ...values.engagement_types,
                                          option,
                                        ]);
                                      } else {
                                        setFieldValue(
                                          "engagement_types",
                                          values.engagement_types.filter((item) => item !== option)
                                        );
                                      }
                                    }}
                                  />
                                  <span className="ct_fs_14 ct_darkblue_text">{option}</span>
                                </label>
                              ))}
                            </div>
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="engagement_types"
                            />
                          </div>
                          <div className="col-md-12">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Project Title
                              </label>
                              <input
                                type="text"
                                className="form-control ct_input"
                                placeholder="Project Title"
                                id="project_title"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.project_title}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="project_title"
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Role in Project
                              </label>
                              <input
                                type="text"
                                className="form-control ct_input"
                                placeholder="Enter Role in Project"
                                id="role_in_project"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.role_in_project}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="role_in_project"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Project Start Date
                              </label>
                              <input
                                type="date"
                                className="form-control ct_input"
                                id="project_start_date"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.project_start_date}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="project_start_date"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Team Member
                              </label>
                              <input
                                className="form-control ct_input"
                                type="text"
                                placeholder="Enter Team Member"
                                id="team_member_required"
                                onBlur={handleBlur}
                                onChange={(event) => {
                                  const onlyDigits = event.target.value.replace(/\D+/g, "");
                                  setFieldValue("team_member_required", onlyDigits);
                                }}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={values.team_member_required}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="team_member_required"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Budget
                              </label>
                              <input
                                type="text"
                                className="form-control ct_input"
                                placeholder="Enter Budget"
                                id="estimated_budget"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.estimated_budget}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="estimated_budget"
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Estimated Project Duration
                              </label>
                              <select
                                className="form-control ct_input"
                                id="project_duration"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.project_duration}
                              >
                                <option value="">Select Duration</option>
                                {projectDurationOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="project_duration"
                              />
                            </div>
                          </div>

                          <TagInput
                            label="Skills"
                            placeholder="Type to add skills..."
                            name="project_skills"
                            value={values.project_skills}
                            onChange={setFieldValue}
                            error={touched.project_skills && errors.project_skills}
                          />

                          <div className="col-md-12">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Project Description
                              </label>
                              <textarea
                                rows="4"
                                className="form-control ct_input h-auto"
                                placeholder="Enter Project Description"
                                id="project_description"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.project_description}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="project_description"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {activeStep === 3 && (
                        <div className="row">
                          <div className="col-12">
                            <h6 className="ct_fs_16 ct_darkblue_text mb-3">
                              Work Environment
                            </h6>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                Preferred Time Zone
                              </label>
                              <select
                                className="form-control ct_input"
                                id="preferred_timezone"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.preferred_timezone}
                              >
                                <option value="">Select Time Zone</option>
                                {timezoneOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="preferred_timezone"
                              />
                            </div>
                          </div>
                          <div className="col-12">
                            <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                              Communication Tools Used
                            </label>
                            <div className="d-flex flex-wrap gap-3 mb-4">
                              {communicationToolsOptions.map((option) => (
                                <label key={option} className="d-flex align-items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={values.communication_tools.includes(option)}
                                    onChange={(event) => {
                                      if (event.target.checked) {
                                        setFieldValue("communication_tools", [
                                          ...values.communication_tools,
                                          option,
                                        ]);
                                      } else {
                                        setFieldValue(
                                          "communication_tools",
                                          values.communication_tools.filter((item) => item !== option)
                                        );
                                      }
                                    }}
                                  />
                                  <span className="ct_fs_14 ct_darkblue_text">{option}</span>
                                </label>
                              ))}
                            </div>
                            {values.communication_tools.includes("Other") && (
                              <div className="form-group mb-4">
                                <input
                                  type="text"
                                  className="form-control ct_input"
                                  placeholder="Other, please specify"
                                  id="communication_other"
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  value={values.communication_other}
                                />
                                <ErrorMessage
                                  errors={errors}
                                  touched={touched}
                                  fieldName="communication_other"
                                />
                              </div>
                            )}
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="communication_tools"
                            />
                          </div>
                          <div className="col-12">
                            <div className="form-group mb-4">
                              <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                                What type of collaboration are you looking for with freelancers?
                              </label>
                              <textarea
                                rows="4"
                                className="form-control ct_input h-auto"
                                placeholder="Describe collaboration"
                                id="collaboration_type"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.collaboration_type}
                              />
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="collaboration_type"
                              />
                            </div>
                          </div>
                          <div className="col-12">
                            <h6 className="ct_fs_16 ct_darkblue_text mb-3">Consent</h6>
                          </div>
                          <div className="col-md-12">
                            <div className="form-group mb-3">
                              <label className="ct_checkbox_label d-flex gap-2">
                                <input
                                  type="checkbox"
                                  id="consent_accuracy"
                                  checked={values.consent_accuracy}
                                  onChange={handleChange}
                                />
                                <span className="ct_fs_14 ct_darkblue_text">
                                  I confirm that the information provided is accurate.
                                </span>
                              </label>
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="consent_accuracy"
                              />
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="form-group mb-3">
                              <label className="ct_checkbox_label d-flex gap-2">
                                <input
                                  type="checkbox"
                                  id="consent_contact"
                                  checked={values.consent_contact}
                                  onChange={handleChange}
                                />
                                <span className="ct_fs_14 ct_darkblue_text">
                                  I agree to be contacted by NUVIO regarding talent matching.
                                </span>
                              </label>
                              <ErrorMessage
                                errors={errors}
                                touched={touched}
                                fieldName="consent_contact"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="d-flex align-items-center justify-content-between pt-4 gap-2">
                        {activeStep > 2 && (
                          <button
                            type="button"
                            className="ct_outline_btn ct_h_44"
                            onClick={() => handlePrevious(values)}
                          >
                            Previous
                          </button>
                        )}
                        {activeStep < steps.length ? (
                          <button
                            type="button"
                            className="ct_yellow_btn ct_h_44 ms-auto"
                            onClick={() => {
                              validateForm().then((formErrors) => {
                                if (Object.keys(formErrors || {}).length) {
                                  setTouched(
                                    Object.keys(formErrors).reduce((acc, key) => {
                                      acc[key] = true;
                                      return acc;
                                    }, {})
                                  );
                                  return;
                                }
                                handleNext(values);
                              });
                            }}
                          >
                            Continue
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="ct_yellow_btn ct_h_44 ms-auto"
                            onClick={() => {
                              validateForm().then((formErrors) => {
                                if (Object.keys(formErrors || {}).length) {
                                  setTouched(
                                    Object.keys(formErrors).reduce((acc, key) => {
                                      acc[key] = true;
                                      return acc;
                                    }, {})
                                  );
                                  return;
                                }
                                handleFinish(values);
                              });
                            }}
                          >
                            Complete Sign Up
                          </button>
                        )}
                      </div>
                    </form>
                  )}
                </Formik>

              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RegisterStep;
